import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { LogIn, MailQuestion } from "lucide-react";
import { useAuth } from "../../auth/AuthContext";
import { supabase } from "../../lib/supabase";

const inputCls =
  "w-full bg-[#FAFBF6] border border-[#B09060]/25 rounded-sm px-4 py-3 text-sm text-[#404040] placeholder-[#9B9B8F] focus:outline-none focus:border-[#B09060] focus:ring-1 focus:ring-[#B09060]/40 transition-colors";
const labelCls = "block text-xs uppercase tracking-[0.2em] font-semibold text-[#8F7648] mb-1.5";

export default function AdminLogin() {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);
  const [forgotMode, setForgotMode] = useState(false);
  const [forgotSent, setForgotSent] = useState(false);
  const { signIn } = useAuth();
  const navigate = useNavigate();

  async function onSubmit(e) {
    e.preventDefault();
    setLoading(true);
    const { error } = await signIn(email, password);
    setLoading(false);
    if (error) {
      toast.error(`Giriş başarısız: ${error.message}`);
    } else {
      toast.success("Giriş başarılı.");
      navigate("/admin");
    }
  }

  async function onForgot(e) {
    e.preventDefault();
    if (!email) {
      toast.error("Lütfen e-posta adresinizi girin.");
      return;
    }
    setLoading(true);
    const { error } = await supabase.auth.resetPasswordForEmail(email, {
      redirectTo: `${window.location.origin}/reset-password`,
    });
    setLoading(false);
    if (error) {
      toast.error(`Gönderilemedi: ${error.message}`);
    } else {
      setForgotSent(true);
      toast.success("E-posta adresiniz kayıtlıysa sıfırlama bağlantısı gönderildi.");
    }
  }

  return (
    <div className="min-h-screen bg-[#F2F3EB] flex items-center justify-center px-6" data-testid="admin-login-page">
      <form
        onSubmit={forgotMode ? onForgot : onSubmit}
        className="w-full max-w-sm bg-white border border-[#B09060]/20 rounded-sm shadow-[0_4px_20px_rgba(176,144,96,0.08)] p-8 space-y-5"
      >
        <div className="flex flex-col items-center text-center">
          <img src="/assets/logo-monogram.svg" alt="VIA PRIME" className="w-14 h-14 object-contain" />
          <h1 className="font-serif-display text-2xl font-medium text-[#404040] mt-3">Yönetim Paneli</h1>
          <p className="text-xs text-[#6B6B6B] mt-1">
            {forgotMode ? "Sıfırlama bağlantısı için e-postanızı girin" : "Devam etmek için giriş yapın"}
          </p>
        </div>
        <div>
          <label className={labelCls} htmlFor="admin-email">E-posta</label>
          <input
            id="admin-email"
            data-testid="admin-email-input"
            type="email"
            required
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            className={inputCls}
          />
        </div>
        {!forgotMode && (
          <div>
            <label className={labelCls} htmlFor="admin-password">Şifre</label>
            <input
              id="admin-password"
              data-testid="admin-password-input"
              type="password"
              required
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              className={inputCls}
            />
          </div>
        )}

        {forgotMode && forgotSent ? (
          <p className="text-xs text-emerald-700 text-center leading-relaxed" data-testid="forgot-sent-message">
            E-posta adresiniz kayıtlıysa sıfırlama bağlantısı gönderildi. Gelen
            bağlantıya tıklayıp yeni şifrenizi belirleyin.
          </p>
        ) : (
          <button
            type="submit"
            disabled={loading}
            data-testid={forgotMode ? "forgot-submit-button" : "admin-login-submit"}
            className="w-full py-3.5 rounded-sm bg-[#B09060] text-[#FAFBF6] font-semibold text-xs uppercase tracking-[0.15em] hover:bg-[#8F7648] transition-colors disabled:opacity-50 flex items-center justify-center gap-2"
          >
            {forgotMode ? (
              <>
                <MailQuestion className="w-4 h-4" strokeWidth={1.5} /> {loading ? "Gönderiliyor..." : "Sıfırlama Bağlantısı Gönder"}
              </>
            ) : (
              <>
                <LogIn className="w-4 h-4" strokeWidth={1.5} /> {loading ? "Giriş yapılıyor..." : "Giriş Yap"}
              </>
            )}
          </button>
        )}

        <button
          type="button"
          onClick={() => { setForgotMode(!forgotMode); setForgotSent(false); }}
          data-testid="forgot-password-toggle"
          className="w-full text-center text-xs text-[#6B6B6B] hover:text-[#8F7648] transition-colors"
        >
          {forgotMode ? "← Giriş ekranına dön" : "Şifremi unuttum"}
        </button>
        <a href="/" className="block text-center text-xs text-[#9B9B8F] hover:text-[#6B6B6B] transition-colors">
          ← Siteye dön
        </a>
      </form>
    </div>
  );
}
